import { useMemo, useState } from 'react';

// ─── Preset calculations ──────────────────────────────────────────────────────

function localIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoMonday(d) {
  const day = new Date(d);
  const dow = day.getDay();
  day.setDate(day.getDate() + (dow === 0 ? -6 : 1 - dow));
  return day;
}

const PRESET_FNS = {
  ytd: () => {
    const y = new Date().getFullYear();
    return { from: `${y}-01-01`, to: localIso(new Date()) };
  },
  this_week: () => {
    const mon = isoMonday(new Date());
    const sun = new Date(mon);
    sun.setDate(sun.getDate() + 6);
    return { from: localIso(mon), to: localIso(sun) };
  },
  last_week: () => {
    const lastMon = new Date(isoMonday(new Date()));
    lastMon.setDate(lastMon.getDate() - 7);
    const lastSun = new Date(lastMon);
    lastSun.setDate(lastSun.getDate() + 6);
    return { from: localIso(lastMon), to: localIso(lastSun) };
  },
  this_month: () => {
    const now = new Date(), y = now.getFullYear(), m = now.getMonth();
    return {
      from: localIso(new Date(y, m, 1)),
      to:   localIso(new Date(y, m + 1, 0)),
    };
  },
  last_month: () => {
    const now = new Date(), y = now.getFullYear(), m = now.getMonth();
    return {
      from: localIso(new Date(y, m - 1, 1)),
      to:   localIso(new Date(y, m, 0)),
    };
  },
  last_year: () => {
    const y = new Date().getFullYear() - 1;
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  },
  all_time: () => ({ from: '', to: '' }),
};

const PRESET_LIST = [
  { key: 'ytd',        label: 'This year' },
  { key: 'last_year',  label: 'Last year' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_week',  label: 'This week' },
  { key: 'last_week',  label: 'Last week' },
  { key: 'all_time',   label: 'All time' },
  { key: 'custom',     label: 'Custom…' },
];

export function getPresetRange(key) {
  return PRESET_FNS[key]?.() ?? { from: '', to: '' };
}

export function detectPreset(from, to) {
  if (!from && !to) return 'all_time';
  for (const { key } of PRESET_LIST) {
    if (key === 'all_time' || key === 'custom') continue;
    const r = PRESET_FNS[key]();
    if (r.from === from && r.to === to) return key;
  }
  return 'custom';
}

export { PRESET_LIST };

// ─── Theme tokens ─────────────────────────────────────────────────────────────

const THEMES = {
  dark: {
    pillBorderActive:   'rgba(255,255,255,0.9)',
    pillBorderInactive: 'rgba(255,255,255,0.25)',
    pillBgActive:       'rgba(255,255,255,0.18)',
    pillBgInactive:     'transparent',
    pillColorActive:    '#fff',
    pillColorInactive:  'rgba(255,255,255,0.6)',
    inputBorder:        '1px solid rgba(255,255,255,0.3)',
    inputBg:            'rgba(255,255,255,0.1)',
    inputColor:         '#fff',
    inputColorScheme:   'dark',
    separatorColor:     'rgba(255,255,255,0.5)',
  },
  light: {
    pillBorderActive:   '#0A7C8A',
    pillBorderInactive: '#D1D5DB',
    pillBgActive:       '#E6F6F7',
    pillBgInactive:     'transparent',
    pillColorActive:    '#0A7C8A',
    pillColorInactive:  '#6B7280',
    inputBorder:        '1px solid #D1D5DB',
    inputBg:            '#fff',
    inputColor:         '#111',
    inputColorScheme:   'light',
    separatorColor:     '#9CA3AF',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────
// variant: 'dark' (default, for T.gradNavy backgrounds) | 'light' (for white/light backgrounds)

export default function DateRangePicker({ dateFrom, dateTo, onChange, variant = 'dark' }) {
  const th = THEMES[variant] ?? THEMES.dark;
  const detectedPreset = useMemo(() => {
    if (!dateFrom && !dateTo) return 'all_time';
    for (const { key } of PRESET_LIST) {
      if (key === 'all_time' || key === 'custom') continue;
      const r = PRESET_FNS[key]();
      if (r.from === dateFrom && r.to === dateTo) return key;
    }
    return 'custom';
  }, [dateFrom, dateTo]);

  const [customOpen, setCustomOpen] = useState(detectedPreset === 'custom');
  const activePreset = customOpen ? 'custom' : detectedPreset;
  const showCustomInputs = activePreset === 'custom';

  return (
    <div style={{ padding: '8px 2px 10px' }}>
      <select
        value={activePreset}
        onChange={e => {
          const key = e.target.value;
          if (key === 'custom') { setCustomOpen(true); return; }
          setCustomOpen(false);
          const r = PRESET_FNS[key]();
          onChange(r.from, r.to);
        }}
        style={{
          width: '100%', padding: '5px 8px', borderRadius: 7,
          border: th.inputBorder, fontSize: 12, fontWeight: 600,
          color: th.inputColor, background: th.inputBg,
          colorScheme: th.inputColorScheme, outline: 'none', cursor: 'pointer',
        }}
      >
        {PRESET_LIST.map(({ key, label }) => (
          <option key={key} value={key}>{label}</option>
        ))}
      </select>
      {showCustomInputs && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
          <input
            type="date"
            value={dateFrom}
            onChange={e => onChange(e.target.value, dateTo)}
            style={{ padding: '5px 8px', borderRadius: 7, border: th.inputBorder, fontSize: 12, color: th.inputColor, background: th.inputBg, outline: 'none', flex: 1, colorScheme: th.inputColorScheme }}
          />
          <span style={{ fontSize: 11, color: th.separatorColor, flexShrink: 0 }}>to</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => onChange(dateFrom, e.target.value)}
            style={{ padding: '5px 8px', borderRadius: 7, border: th.inputBorder, fontSize: 12, color: th.inputColor, background: th.inputBg, outline: 'none', flex: 1, colorScheme: th.inputColorScheme }}
          />
        </div>
      )}
    </div>
  );
}
